import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { communityTrpc } from '@org/clients/community';
import {
  Button,
  Input,
  Label,
  MediaUploader,
  RadioGroup,
  RadioGroupItem,
  RichTextEditor,
  useToast,
} from '@org/ui';
import type { RichTextDoc } from '@org/model';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useMentionSearch } from '@/hooks/use-mention-search';
import { useRichTextMediaUpload } from '@/hooks/use-rich-text-media-upload';
import { EMPTY_DOC } from '@/utils/rich-text-empty';

type BannerMode = 'none' | 'image' | 'video';

interface SubPageEditorProps {
  spaceId: string;
  subPageId?: string;
  initialTitle?: string;
  initialBodyJson?: RichTextDoc | null;
  initialBannerImageUrl?: string | null;
  initialBannerVideoUrl?: string | null;
}

export function SubPageEditor({
  spaceId,
  subPageId,
  initialTitle = '',
  initialBodyJson = null,
  initialBannerImageUrl = null,
  initialBannerVideoUrl = null,
}: SubPageEditorProps) {
  const { t } = useTranslation('community');
  const navigate = useNavigate();
  const { toast } = useToast();

  const [title, setTitle] = useState(initialTitle);
  const [bodyDoc, setBodyDoc] = useState<RichTextDoc>(
    (initialBodyJson as RichTextDoc | null) ?? EMPTY_DOC
  );
  const [bannerImageUrl, setBannerImageUrl] = useState<string | null>(
    initialBannerImageUrl
  );
  const [bannerVideoUrl, setBannerVideoUrl] = useState<string | null>(
    initialBannerVideoUrl
  );
  const [bannerMode, setBannerMode] = useState<BannerMode>(
    initialBannerImageUrl ? 'image' : initialBannerVideoUrl ? 'video' : 'none'
  );

  const searchUsers = useMentionSearch();
  const uploadMedia = useRichTextMediaUpload();

  const isUpdate = !!subPageId;
  const utils = communityTrpc.useUtils();

  const { data: existingPages } = communityTrpc.spaces.subPages.list.useQuery(
    { spaceId },
    { enabled: !isUpdate }
  );
  const atPageCap = !isUpdate && (existingPages?.length ?? 0) >= 3;

  const createMutation = communityTrpc.spaces.subPages.create.useMutation({
    onSuccess: (data) => {
      utils.spaces.subPages.list.invalidate({ spaceId });
      utils.pinnedSpaces.list.invalidate();
      toast({ title: t('subPages.createSuccess') });
      navigate({
        to: '/spaces/$spaceId/pages/$pageId',
        params: { spaceId, pageId: data.subPageId },
      });
    },
    onError: (error) => {
      const isMaxPages = error.message?.includes('Maximum');
      toast({
        title: isMaxPages
          ? t('subPages.maxPagesWarning')
          : t('common.error'),
        description: isMaxPages ? undefined : error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = communityTrpc.spaces.subPages.update.useMutation({
    onSuccess: () => {
      utils.spaces.subPages.list.invalidate({ spaceId });
      utils.spaces.subPages.get.invalidate({ subPageId: subPageId! });
      utils.pinnedSpaces.list.invalidate();
      toast({ title: t('subPages.updateSuccess') });
      navigate({
        to: '/spaces/$spaceId/pages/$pageId',
        params: { spaceId, pageId: subPageId! },
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleBannerModeChange = (next: BannerMode) => {
    setBannerMode(next);
    if (next !== 'image') setBannerImageUrl(null);
    if (next !== 'video') setBannerVideoUrl(null);
  };

  const handleSave = () => {
    if (!title.trim()) return;

    if (bannerMode === 'image' && !bannerImageUrl) {
      toast({
        title: t('subPages.bannerImageRequired'),
        variant: 'destructive',
      });
      return;
    }
    if (bannerMode === 'video' && !bannerVideoUrl) {
      toast({
        title: t('subPages.bannerVideoRequired'),
        variant: 'destructive',
      });
      return;
    }

    const payload = {
      title: title.trim(),
      bodyJson: bodyDoc as unknown as Record<string, unknown>,
      bannerImageUrl: bannerMode === 'image' ? bannerImageUrl : null,
      bannerVideoUrl: bannerMode === 'video' ? bannerVideoUrl : null,
    };

    if (isUpdate) {
      updateMutation.mutate({ subPageId: subPageId!, ...payload });
    } else {
      createMutation.mutate({ spaceId, ...payload });
    }
  };

  const handleDiscard = () => {
    navigate({ to: '/spaces/$spaceId', params: { spaceId } });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label>{t('subPages.bannerLabel')}</Label>
        <RadioGroup
          value={bannerMode}
          onValueChange={(v) => handleBannerModeChange(v as BannerMode)}
        >
          <div className="flex flex-wrap gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value="none" id="banner-none" />
              {t('subPages.bannerNone')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value="image" id="banner-image" />
              {t('subPages.bannerImage')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value="video" id="banner-video" />
              {t('subPages.bannerVideo')}
            </label>
          </div>
        </RadioGroup>

        {bannerMode === 'image' && (
          bannerImageUrl ? (
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={bannerImageUrl}
                alt=""
                className="h-48 w-full object-cover sm:h-64"
              />
              <Button
                variant="destructive"
                size="sm"
                className="absolute right-3 top-3 gap-1.5"
                onClick={() => setBannerImageUrl(null)}
                aria-label={t('subPages.bannerRemove')}
              >
                <X className="h-4 w-4" />
                {t('subPages.bannerRemove')}
              </Button>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card p-4">
              <MediaUploader
                onUploadComplete={(url) => setBannerImageUrl(url)}
                acceptedFileTypes={['image/jpeg', 'image/png', 'image/webp', 'image/gif']}
                maxFileSize={10 * 1024 * 1024}
                compact
              />
            </div>
          )
        )}

        {bannerMode === 'video' && (
          bannerVideoUrl ? (
            <div className="relative overflow-hidden rounded-2xl">
              <video
                src={bannerVideoUrl}
                controls
                preload="metadata"
                className="w-full rounded-2xl"
              />
              <Button
                variant="destructive"
                size="sm"
                className="absolute right-3 top-3 gap-1.5"
                onClick={() => setBannerVideoUrl(null)}
                aria-label={t('subPages.bannerRemove')}
              >
                <X className="h-4 w-4" />
                {t('subPages.bannerRemove')}
              </Button>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card p-4">
              <MediaUploader
                onUploadComplete={(url) => setBannerVideoUrl(url)}
                acceptedFileTypes={['video/mp4', 'video/webm', 'video/quicktime']}
                compact
              />
            </div>
          )
        )}
      </div>

      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('subPages.titlePlaceholder')}
        className="border-none bg-transparent text-2xl font-bold placeholder:text-muted-foreground/50 focus-visible:ring-0"
        aria-label={t('subPages.titlePlaceholder')}
      />

      <RichTextEditor
        value={bodyDoc}
        onChange={setBodyDoc}
        searchUsers={searchUsers}
        uploadMedia={uploadMedia}
        placeholder={t('subPages.bodyPlaceholder')}
        minHeight={400}
      />

      {atPageCap && (
        <p className="text-sm text-destructive" role="alert">
          {t('subPages.maxPagesWarning')}
        </p>
      )}

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={handleDiscard}>
          {t('subPages.discard')}
        </Button>

        <Button
          onClick={handleSave}
          disabled={isSaving || !title.trim() || atPageCap}
          className="gap-2"
        >
          {isSaving ? t('subPages.saving') : t('subPages.save')}
        </Button>
      </div>
    </div>
  );
}
